import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import { preprocessMath } from "../utils/mathPreprocess";

/** Convert ==text== highlight syntax to HTML mark tags before rendering. */
function applyHighlight(text) {
  if (!text) return text;
  return text.replace(/==([^=\n]+)==/g, "<mark>$1</mark>");
}

/** Strip markdown/LaTeX formatting leaving plain text suitable for TTS. */
function toSpeakable(text) {
  if (!text) return "";
  return text
    // Remove display math blocks
    .replace(/\$\$[\s\S]*?\$\$/g, " (formula) ")
    // Remove inline math
    .replace(/\$[^$\n]+?\$/g, " (formula) ")
    // Remove escaped brackets
    .replace(/\\\[[\s\S]*?\\\]/g, " (formula) ")
    .replace(/\\\([\s\S]*?\\\)/g, " (formula) ")
    // Remove markdown headings hashes
    .replace(/^#{1,6}\s+/gm, "")
    // Remove bold/italic markers
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, "$1")
    // Remove inline code
    .replace(/`[^`]+`/g, "")
    // Remove links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove horizontal rules
    .replace(/^[-*_]{3,}\s*$/gm, "")
    // Collapse extra whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export default function AiResult({ content, onAddNote }) {
  const [speaking, setSpeaking] = useState(false);
  const utterRef = useRef(null);

  // Stop speech when component unmounts or content changes
  useEffect(() => {
    return () => { window.speechSynthesis?.cancel(); };
  }, [content]);

  const toggleSpeak = () => {
    if (!window.speechSynthesis) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const utter = new SpeechSynthesisUtterance(toSpeakable(content));
    utter.lang = "en-US";
    utter.rate = 0.95;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
    setSpeaking(true);
  };

  return (
    <div className="ai-result">
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
      >
        {applyHighlight(preprocessMath(content))}
      </ReactMarkdown>
      <div className="ai-result-actions">
        <button
          className={`read-aloud-btn${speaking ? " speaking" : ""}`}
          onClick={toggleSpeak}
          title={speaking ? "Stop reading" : "Read aloud"}
        >
          {speaking ? "⏹ Stop" : "🔊 Read Aloud"}
        </button>
        {onAddNote && (
          <button className="add-note-btn" onClick={onAddNote}>
            📌 Add to Notes
          </button>
        )}
      </div>
    </div>
  );
}
